import { useMemo } from 'react';

import type { FilteredEmployee } from '@/hooks/filters/useRosterFilter';
import type { ObjectiveOrDraft } from '@/types/objectives';
import type { HeldObjective } from '@/utils/pillarSummariesAmong';

import { PillarScope } from '@/constants/PillarScope';
import { defaultFiltersState } from '@/hooks/filters/useCompanyAttributesFilters';
import { useRosterFilter } from '@/hooks/filters/useRosterFilter';
import { useAddressedListFilters } from '@/hooks/objectives/useAddressedListFilters';
import { useObjectiveAbilities } from '@/hooks/objectives/useObjectiveAbilities';
import { useObjectiveOwners } from '@/hooks/objectives/useObjectiveOwners';
import { useObjectiveStatusFilter } from '@/hooks/objectives/useObjectiveStatusFilter';
import { usePillarScope } from '@/hooks/objectives/usePillarScope';
import { isPublishedObjective } from '@/utils/objectiveStatus';

/** No state picked: the list keeps every state. */
const EVERY_STATUS: string[] = [];

/** Where the read of the objectives handed to the lens stands. */
export interface ObjectivesRead {
    isLoading: boolean;
    hasError: boolean;
}

/** The people of the reader's team the filters keep, as the sentences under the filters name them. */
export interface FilteredTeam {
    employees: FilteredEmployee[];
    /** The filters, or whose reach the reader has, are still on their way: no count to give yet. */
    isResolving: boolean;
}

/**
 * How the objectives page and a pillar's page read their objectives, the one way both do: the
 * filters and the pillar scope from the address, the list those filters keep, grouped by person,
 * and the objectives the pillars are counted among in the scope in effect.
 *
 * Under "my team", the list is narrowed by the manager filter on the reader (`usePillarScope`) alone,
 * and an objective counts among the team members who hold it; under "selected
 * filters", among the people whose group keeps it in the list below. Under "organization" the
 * figures are the server's, company-wide: `heldObjectives` is then empty and unused.
 *
 * `scopedFigures` says whether the pillar figures of the scope in effect can be shown yet, beyond
 * the server's own totals, which each page reads on its own.
 */
export const useObjectiveListLens = (
    objectives: ObjectiveOrDraft[],
    objectivesRead: ObjectivesRead,
) => {
    const pillarScope = usePillarScope();
    const filters = useAddressedListFilters({ withEachChange: pillarScope.followFilters });
    // Under "my team", the list is the whole team, as the pillars are: the other filters stay in
    // the address and in the toolbar, and apply again under "selected filters".
    const isTeamScope = pillarScope.scope === PillarScope.TEAM;
    const appliedFilters = useMemo(
        () =>
            isTeamScope
                ? {
                      attributeFilters: filters.attributeFilters && {
                          ...defaultFiltersState,
                          managers: filters.attributeFilters.managers,
                      },
                      searchText: '',
                      statuses: EVERY_STATUS,
                  }
                : {
                      attributeFilters: filters.attributeFilters,
                      searchText: filters.searchText,
                      statuses: filters.selectedStatuses,
                  },
        [isTeamScope, filters.attributeFilters, filters.searchText, filters.selectedStatuses],
    );
    const roster = useRosterFilter(appliedFilters.attributeFilters);
    const { hasCompanyWideReach, isLoading: isLoadingAbilities } = useObjectiveAbilities();
    const owners = useObjectiveOwners(
        objectives,
        appliedFilters.searchText,
        roster.filterEmployeeIds,
    );
    const statusFilter = useObjectiveStatusFilter(owners.groups, {
        selectedValues: filters.selectedStatuses,
        appliedValues: appliedFilters.statuses,
        onChange: filters.setSelectedStatuses,
    });

    const heldObjectives = useMemo<HeldObjective[]>(() => {
        if (pillarScope.scope === PillarScope.TEAM) {
            return objectives.flatMap((objective) =>
                [...new Set(objective.visibleHolderEmployeeIds)]
                    .filter((holderId) => pillarScope.teamIds.has(holderId))
                    .map((holderId) => ({ holderId, objective })),
            );
        }

        if (pillarScope.scope === PillarScope.FILTERS) {
            return statusFilter.groups.flatMap(({ ownerId, objectives: kept }) =>
                ownerId ? kept.map((objective) => ({ holderId: ownerId, objective })) : [],
            );
        }

        return [];
    }, [pillarScope.scope, pillarScope.teamIds, objectives, statusFilter.groups]);

    // Under "selected filters", a list that keeps drafts alone counts in no pillar: the scope bar
    // says why every tile reads zero.
    const keepsDraftsOnly =
        pillarScope.scope === PillarScope.FILTERS &&
        statusFilter.groups.length > 0 &&
        statusFilter.groups.every(({ objectives: kept }) => !kept.some(isPublishedObjective));

    // The figures wait for, and fail with, exactly what `heldObjectives` is built from: the
    // objectives under "my team"; under "selected filters", also the people the filters resolve to
    // and the employee batch the list is grouped by.
    const countsAmongObjectives = pillarScope.scope !== PillarScope.ORGANIZATION;
    const countsAmongFilters = pillarScope.scope === PillarScope.FILTERS;
    const scopedFigures = {
        isLoading:
            pillarScope.isResolvingScope ||
            (countsAmongObjectives && objectivesRead.isLoading) ||
            (countsAmongFilters && (owners.isLoading || roster.isResolvingFilter)),
        hasError:
            (countsAmongObjectives && objectivesRead.hasError) ||
            (countsAmongFilters && (!!owners.error || roster.hasFilterError)),
    };

    // A reader who sees the whole company reads the filters' sentences as they are; anyone else is
    // told the filters keep people of their team, and which.
    const isTeamReach = !hasCompanyWideReach;
    const filteredTeam: FilteredTeam | undefined =
        isTeamReach && roster.isFilterActive
            ? {
                  employees: roster.filterEmployees ?? [],
                  isResolving: isLoadingAbilities || roster.isResolvingFilter,
              }
            : undefined;

    return {
        filters,
        roster,
        owners,
        statusFilter,
        pillarScope,
        heldObjectives,
        keepsDraftsOnly,
        scopedFigures,
        isTeamReach,
        filteredTeam,
    };
};
